import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faVideo } from "@fortawesome/free-solid-svg-icons";

import pluginService from "api/pluginService";
import { ICameraDraft, ICameraPresetDraft } from "./cameraDrafts";
import styles from "./styles.module.scss";

// Konfiguracja kamer PTZ — odpowiednik zakładki „Konfiguracja kamer" z okna ustawień
// Biura rady w Magistracie (Settings/CouncilorsList.xaml + DahuaSettingsViewModel).
//
// Kamery i presety edytuje się jako całość i zapisuje razem z resztą ustawień
// portalu — tak samo jak w Magistracie, gdzie były jedną serializowaną tablicą.
//
// Test połączenia idzie przez wtyczkę desktop, bo kamera stoi w sieci urzędu
// i ani przeglądarka, ani serwer Nota jej nie dosięgną.

interface Props {
  cameras: ICameraDraft[];
  onChange: (cameras: ICameraDraft[]) => void;
}

const CamerasSection = ({ cameras, onChange }: Props) => {
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  const patch = (key: string, change: Partial<ICameraDraft>) =>
    onChange(cameras.map((c) => (c.Key === key ? { ...c, ...change } : c)));

  const patchPreset = (
    key: string,
    index: number,
    change: Partial<ICameraPresetDraft>,
  ) =>
    onChange(
      cameras.map((c) =>
        c.Key === key
          ? {
              ...c,
              Presets: c.Presets.map((p, i) => (i === index ? { ...p, ...change } : p)),
            }
          : c,
      ),
    );

  const addCamera = () =>
    onChange([
      ...cameras,
      {
        Key: `camera-new-${Date.now()}-${cameras.length}`,
        Address: "",
        Login: "",
        Password: "",
        HasPassword: false,
        RemovePassword: false,
        Scene: "",
        Presets: [],
      },
    ]);

  const test = async (camera: ICameraDraft) => {
    setTesting(camera.Key);
    setTestResult((prev) => ({ ...prev, [camera.Key]: "" }));

    try {
      const result = await pluginService.testCamera({
        CameraId: camera.Id ?? null,
        Address: camera.Address.trim() || null,
        Login: camera.Login.trim() || null,
        Password: camera.Password || null,
      });

      setTestResult((prev) => ({
        ...prev,
        [camera.Key]: result.Ok
          ? result.CurrentPreset != null
            ? `Połączono. Kamera stoi na presecie ${result.CurrentPreset}.`
            : "Połączono. Kamera nie podaje aktualnego presetu."
          : (result.Error ?? "Nie udało się połączyć z kamerą."),
      }));
    } catch {
      setTestResult((prev) => ({
        ...prev,
        [camera.Key]:
          "Nie udało się porozmawiać z wtyczką. Test kamery działa tylko na stanowisku z uruchomioną wtyczką Nota.",
      }));
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className={styles.cameraList}>
      {cameras.map((camera, index) => (
        <div key={camera.Key} className={styles.cameraCard}>
          <div className={styles.cameraCardHeader}>
            <FontAwesomeIcon icon={faVideo} />
            <span className={styles.cameraCardTitle}>Kamera #{index + 1}</span>
            <button
              type="button"
              className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
              title="Usuń kamerę"
              onClick={() => onChange(cameras.filter((c) => c.Key !== camera.Key))}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Adres sieciowy</label>
              <input
                className={styles.textInput}
                value={camera.Address}
                onChange={(e) => patch(camera.Key, { Address: e.target.value })}
                placeholder="np. 192.168.1.192"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Login</label>
              <input
                className={styles.textInput}
                value={camera.Login}
                onChange={(e) => patch(camera.Key, { Login: e.target.value })}
                placeholder="np. admin"
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Hasło</label>
              <input
                className={styles.textInput}
                type="password"
                autoComplete="new-password"
                value={camera.Password}
                disabled={camera.RemovePassword}
                onChange={(e) => patch(camera.Key, { Password: e.target.value })}
                placeholder={camera.HasPassword ? "bez zmiany" : "hasło do kamery"}
              />
              {camera.HasPassword && (
                <label className={styles.radioOption}>
                  <input
                    type="checkbox"
                    checked={camera.RemovePassword}
                    onChange={(e) =>
                      patch(camera.Key, {
                        RemovePassword: e.target.checked,
                        Password: "",
                      })
                    }
                  />
                  Usuń zapisane hasło
                </label>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Scena OBS z tą kamerą</label>
              <input
                className={styles.textInput}
                value={camera.Scene}
                onChange={(e) => patch(camera.Key, { Scene: e.target.value })}
                placeholder="np. Kamera sala"
              />
              <span className={styles.fieldLabel}>
                Najazd na preset tej kamery przełączy OBS na tę scenę.
              </span>
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Predefiniowane widoki</label>

            {camera.Presets.map((preset, presetIndex) => (
              <div key={preset.Id ?? `preset-${presetIndex}`} className={styles.optionRow}>
                <input
                  className={styles.textInput}
                  value={preset.Name}
                  onChange={(e) =>
                    patchPreset(camera.Key, presetIndex, { Name: e.target.value })
                  }
                  placeholder="np. Mównica"
                />
                <input
                  className={`${styles.textInput} ${styles.presetNumberInput}`}
                  value={preset.Number}
                  inputMode="numeric"
                  onChange={(e) =>
                    patchPreset(camera.Key, presetIndex, {
                      Number: e.target.value.replace(/[^0-9]/g, ""),
                    })
                  }
                  placeholder="nr"
                />
                <button
                  type="button"
                  className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                  title="Usuń widok"
                  onClick={() =>
                    patch(camera.Key, {
                      Presets: camera.Presets.filter((_, i) => i !== presetIndex),
                    })
                  }
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}

            <button
              type="button"
              className={styles.addOptionButton}
              onClick={() =>
                patch(camera.Key, {
                  Presets: [...camera.Presets, { Name: "", Number: "" }],
                })
              }
            >
              <FontAwesomeIcon icon={faPlus} />
              Dodaj widok
            </button>

            {index === 0 && (
              <span className={styles.fieldLabel}>
                Pierwszy widok pierwszej kamery jest „widokiem ogólnym" — na niego
                kamera wraca przy przejściu do punktu porządku obrad i po zamknięciu
                głosowania. Pozostałe widoki prowadzący wybiera przyciskiem w panelu
                „Prowadzenie obrad".
              </span>
            )}
          </div>

          <div className={styles.cameraCardFooter}>
            <button
              type="button"
              className={styles.conductAction}
              disabled={testing === camera.Key || !camera.Address.trim()}
              onClick={() => test(camera)}
            >
              {testing === camera.Key ? "Łączenie…" : "Test połączenia"}
            </button>

            {!!testResult[camera.Key] && (
              <span className={styles.fieldLabel}>{testResult[camera.Key]}</span>
            )}
          </div>
        </div>
      ))}

      <button type="button" className={styles.addOptionButton} onClick={addCamera}>
        <FontAwesomeIcon icon={faPlus} />
        Dodaj kamerę
      </button>
    </div>
  );
};

export default CamerasSection;
